import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CuisineType } from '../models/cuisine';
import { Observable, switchMap, of, BehaviorSubject, map, tap } from 'rxjs';
import { ChainType } from '../models/chain';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { environment } from 'environments/environment';
import { Table } from '../models/tables';
import { TranslocoService } from '@ngneat/transloco';
import { Name } from '../models/name';

@Injectable({
  providedIn: 'root'
})
export class ChainService {

  private _apiLink = environment.apiLink;

  private _tables: BehaviorSubject<Table[]> = new BehaviorSubject<Table[]>([]);

  setTables(array: Table[]) {
    this._tables.next(array)
  }

  get tables$() {
    return this._tables.asObservable()
  }

  private _qrQodeName: BehaviorSubject<Name> = new BehaviorSubject<Name>(null);

  setQrCodeName(value: Name) {
    this._qrQodeName.next(value)
  }

  get qrQodeName$() {
    return this._qrQodeName.asObservable()
  }
  constructor(private _httpClient: HttpClient, private _translocoService: TranslocoService) { }

  getChain(restaurantId: number, name?: string): Observable<ChainType[]> {
    let params = new HttpParams()
    if (name) {
      params = params.append('name', name)

    }
    return this._httpClient.get<ChainType[]>(`${this._apiLink}chain/${restaurantId}`, { params }).pipe(
      switchMap((response: any) => {
        this.setChainList(response.data.chains)
        return of(response.data.chains)
      })
    );
  }

  getChainCurrency(chainId: number) {
    return this._httpClient.get<ChainType[]>(`${this._apiLink}additions/${chainId}/get-currency`)
  }


  private _selectedChain: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  get selectedChain$(): Observable<number> {
    return this._selectedChain.asObservable();
  }

  setSelectedChain(chainId: number) {
    this._selectedChain.next(chainId)
  }


  selectMainChain(modal) {
    return this._httpClient.put(`${this._apiLink}restaurants/selected-chain`, modal).pipe(
      tap((response: any) => {
        this.setSelectedChain(response.data.selectedChain)
      }),
    );

  }
  getSelectChain() {
    return this._httpClient.get(`${this._apiLink}restaurants/selected-chain`)
  }

  private _chainLsit: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  get chainList$(): Observable<any> {
    return this._chainLsit.asObservable();
  }

  setChainList(array: any[]) {
    this._chainLsit.next(array);
  }

  getChainForMenu() {
    return this._httpClient.get<ChainType[]>(`${this._apiLink}chain/`).pipe(
      switchMap((response: any) => {
        this.setChainList(response.data.chains)
        return of(response.data.chains)
      })
    );
  }

  getChainForNavigation(restaurantId: number): Observable<FuseNavigationItem[]> {
    return this._translocoService.langChanges$.pipe(
      switchMap(lang => {
        return this._httpClient.get<any[]>(`${this._apiLink}chain/${restaurantId}`).pipe(
          map((response: any) => {
            const chains: any[] = response.data.chains;
            const navigationItems: FuseNavigationItem[] = chains.map(chain => ({
              id: `chain${chain.id.toString()}`,
              title: chain.name,
              type: 'basic',
              link: `/menu/${chain.id}`
            }));
            return navigationItems;
          })
        );
      })
    );
  }


  addChain(chain: ChainType) {
    return this._httpClient.post(this._apiLink + 'chain/new', chain)
  }

  editChain(chain: ChainType, id: number) {

    return this._httpClient.put(`${this._apiLink}chain/${id}/edit`, chain);
  }

  deleteChain(id: number) {
    return this._httpClient.delete(`${this._apiLink}chain/${id}/delete`);
  }

  getChainById(chainId: number, resId: number) {
    return this._httpClient.get(`${this._apiLink}chain/${resId}/${chainId}`);

  }

  getChainStaff(chainId){
    return this._httpClient.get<ChainType[]>(`${this._apiLink}chain/${chainId}/staff`).pipe(
      switchMap((response: any) => {
        return of(response.data)
      })
    );
  }
  getCuisineById(id: number) {
    return this._httpClient.get<CuisineType>(`${this._apiLink}chain/get/currency/${id}`);
  }

  addTable(chainId: number) {
    return this._httpClient.post(`${this._apiLink}chain/${chainId}/new/tables`, {});
  }

  getTables(chainId: number) {
    return this._httpClient.get(`${this._apiLink}chain/${chainId}/tables`).pipe(
      switchMap((response: any) => {
        this.setTables(response.data.tablesData)
        this.setQrCodeName(response.data.qrcodeName)
        return of(response.data.tablesData)
      })
    );;
  }

  editTable(chainId, tableId, modal){
    return this._httpClient.put(`${this._apiLink}chain/${chainId}/edit/table/${tableId}`,modal)
  }

  deleteTable(chainId: number, tableId: number) {
    return this._httpClient.delete(`${this._apiLink}chain/${chainId}/delete/table/${tableId}`);
  }

  getIngrediants(chainId) {
    return this._httpClient.get<ChainType[]>(`${this._apiLink}restaurants/ingredients?chainId=${chainId}`).pipe(
      switchMap((response: any) => {
        return of(response.data.uniqueIngredients)
      })
    );
  }

  getApi(chainId: number) {
    return this._httpClient.get<ChainType[]>(`${this._apiLink}external-api/${chainId}/documentation`).pipe(
      switchMap((response: any) => {
        return of(response.data.documentation)
      })
    );
  }

  getChainManager() {
    return this._httpClient.get(this._apiLink + 'managers/managers')
  }

  addChainManager(modal: any) {
    return this._httpClient.post(this._apiLink + 'managers/new-manager', modal);
  }
  editManager(chainId: number, managerId: number, modal) {

    return this._httpClient.put(`${this._apiLink}managers/${chainId}/${managerId}/edit-manager`, modal);
  }

  deleteManager(chainId: number, managerId: number) {
    return this._httpClient.delete(`${this._apiLink}managers/${chainId}/${managerId}/delete-manager`);

  }

  getChainKitchen() {
    return this._httpClient.get(this._apiLink + 'kitchens')
  }

  addChainKitchen(modal: any) {
    return this._httpClient.post(this._apiLink + 'kitchens/new-kitchen', modal);
  }
  editKitchen(chainId: number, kitchenId: number, modal) {
    return this._httpClient.put(`${this._apiLink}kitchens/${chainId}/${kitchenId}/edit-kitchen`, modal);
  }

  deleteKitchen(chainId: number, kitchenId: number) {
    return this._httpClient.delete(`${this._apiLink}kitchens/${chainId}/${kitchenId}/delete-kitchen`);
  }

  getChainCachier() {
    return this._httpClient.get(this._apiLink + 'cachiers')
  }

  addChainCachier(modal: any) {
    return this._httpClient.post(this._apiLink + 'cachiers/new-cachier', modal);
  }
  editCachier(chainId: number, cachierId: number, modal) {
    return this._httpClient.put(`${this._apiLink}cachiers/${chainId}/${cachierId}/edit-cachier`, modal);
  }

  deleteCachier(chainId: number, cachierId: number) {
    return this._httpClient.delete(`${this._apiLink}cachiers/${chainId}/${cachierId}/delete-cachier`);
  }

  getChainWaiter() {
    return this._httpClient.get(this._apiLink + 'waiters')
  }

  addChainWaiter(modal: any) {
    return this._httpClient.post(this._apiLink + 'waiters/new-waiter', modal);
  }
  editWaiter(chainId: number, waiterId: number, modal) {
    return this._httpClient.put(`${this._apiLink}waiters/${chainId}/${waiterId}/edit-waiter`, modal);
  }

  deleteWaiter(chainId: number, waiterId: number) {
    return this._httpClient.delete(`${this._apiLink}waiters/${chainId}/${waiterId}/delete-waiter`);
  }

  downloadQrCode(chainId: number, modal: any, responseType: 'blob' | 'json' = 'json') {
    return this._httpClient.post(`${this._apiLink}chain/${chainId}/image`, modal, { responseType } as any);
  }
}
